/**
 * Content Studio — bucket-driven content creation for @flaxitup + LinkedIn.
 * Phase 1 ships two tabs: Buckets (review + refresh Drive refs) and
 * Composer (4-step wizard: setup → prompt → image → caption → download).
 * Calendar + Insights + Publishing are stubbed for later phases.
 */
import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { SectionHeader } from '@/components/charts/SectionHeader';
import { cn } from '@/lib/utils';
import { BucketsTab } from './BucketsTab';
import { ComposerWizard } from './ComposerWizard';
import { DraftsTab } from './DraftsTab';
import { IgGridTab } from './IgGridTab';
import { CalendarTab } from './CalendarTab';
import { InsightsTab } from './InsightsTab';
import { CommentsTab } from './CommentsTab';
import { AdsTab } from './AdsTab';
import { DmsTab } from './DmsTab';
import { VideoRefsTab } from './VideoRefsTab';

type Tab = 'composer' | 'drafts' | 'calendar' | 'insights' | 'ig' | 'comments' | 'dms' | 'ads' | 'refs' | 'buckets';

const TABS: Array<{ id: Tab; label: string; enabled: boolean; hint?: string }> = [
  { id: 'composer', label: 'Composer', enabled: true },
  { id: 'drafts',   label: 'Drafts',   enabled: true },
  { id: 'calendar', label: 'Calendar', enabled: true },
  { id: 'insights', label: 'Insights', enabled: true },
  { id: 'ig',       label: 'IG grid',  enabled: true },
  { id: 'comments', label: 'Comments', enabled: true },
  { id: 'dms',      label: 'DMs',      enabled: true },
  { id: 'ads',      label: 'Ads',      enabled: true },
  { id: 'refs',     label: 'Video refs', enabled: true },
  { id: 'buckets',  label: 'Buckets',  enabled: true },
];

export function StudioPage() {
  const [tab, setTab] = useState<Tab>('composer');

  return (
    <div className="space-y-6">
      <SectionHeader
        title={
          <span className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-violet-500" />
            Content Studio
          </span>
        }
        description="Bucket-driven Instagram + LinkedIn pipeline. References live in Drive, prompts come from Claude, images from Nano Banana Pro."
      />

      <div className="flex flex-wrap gap-1 border-b border-border/40">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => t.enabled && setTab(t.id)}
            disabled={!t.enabled}
            className={cn(
              'relative -mb-px px-3 py-2 text-sm font-medium transition-colors',
              t.enabled
                ? tab === t.id
                  ? 'border-b-2 border-violet-500 text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
                : 'cursor-not-allowed text-muted-foreground/40',
            )}
          >
            {t.label}
            {t.hint && (
              <span className="ml-1.5 rounded bg-muted px-1.5 py-0.5 text-2xs text-muted-foreground/70">
                {t.hint}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === 'composer' && <ComposerWizard />}
      {tab === 'drafts' && <DraftsTab />}
      {tab === 'calendar' && <CalendarTab />}
      {tab === 'insights' && <InsightsTab />}
      {tab === 'ig' && <IgGridTab />}
      {tab === 'comments' && <CommentsTab />}
      {tab === 'dms' && <DmsTab />}
      {tab === 'ads' && <AdsTab />}
      {tab === 'refs' && <VideoRefsTab />}
      {tab === 'buckets' && <BucketsTab />}
    </div>
  );
}
